import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import routeFacts from '../../../data/routeFacts.json'
import { getRouteFacts, isTier1Route, titleCase } from '../../../utils/routeData'
import { routePath } from '../../../utils/urls'
import { slugify } from '../../../utils/slugify'

/**
 * Popular Intercity Routes, rebuilt as a priced route grid.
 *
 * Every card shows a REAL fare: distance, duration and starting price come from
 * src/data/routeFacts.json (the same source RouteShowcase uses), never invented.
 * Tier-1 routes link straight to their live route/fare page; the rest prefill
 * the booking form on the homepage, so a crawler is never handed a thin page.
 * If we can geolocate the visitor and their city has enough real routes, the
 * grid personalises to "routes from <their city>"; otherwise it shows a curated
 * national set. All pairs below are verified present in routeFacts.
 */
const FEATURED = [
  ['mumbai', 'pune'],
  ['delhi', 'jaipur'],
  ['bangalore', 'mysore'],
  ['chennai', 'tirupati'],
  ['ahmedabad', 'udaipur'],
  ['surat', 'ahmedabad'],
  ['pune', 'nashik'],
  ['delhi', 'agra'],
  ['jaipur', 'udaipur'],
  ['lucknow', 'varanasi'],
  ['vadodara', 'ahmedabad'],
  ['chandigarh', 'shimla'],
]

// Real routes originating from a city, cheapest (nearest) first.
const routesForCity = (citySlug) => {
  const prefix = `${citySlug}|`
  return Object.keys(routeFacts)
    .filter((k) => k.startsWith(prefix))
    .map((k) => {
      const [from, to] = k.split('|')
      return { from, to, facts: routeFacts[k] }
    })
    .sort((a, b) => (a.facts.distanceKm || 0) - (b.facts.distanceKm || 0))
}

const RoadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#21a5a0" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19 8 5" /><path d="m16 5 4 14" /><path d="M12 5v2" /><path d="M12 11v2" /><path d="M12 17v2" />
  </svg>
)
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#21a5a0" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
  </svg>
)
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
)

const PopularRoutes = () => {
  const { location } = useSelector((state) => state.location)
  const navigate = useNavigate()

  const userCity = location?.city || null
  const citySlug = userCity ? slugify(userCity) : null

  const { routes, personalised } = useMemo(() => {
    const cityRoutes = citySlug ? routesForCity(citySlug) : []
    if (cityRoutes.length >= 6) {
      return { routes: cityRoutes.slice(0, 12), personalised: true }
    }
    const featured = FEATURED.map(([from, to]) => ({ from, to, facts: getRouteFacts(from, to) }))
    return { routes: featured, personalised: false }
  }, [citySlug])

  const prefillSearch = (from, to) => {
    navigate('/', { state: { routePrefill: { fromCity: from, toCity: to } } })
    setTimeout(() => {
      document.querySelector('.home-main')?.scrollIntoView({ behavior: 'smooth' })
    }, 50)
  }

  const scrollToSearch = () => {
    document.querySelector('.home-main')?.scrollIntoView({ behavior: 'smooth' })
  }

  const renderCard = ({ from, to, facts }) => {
    const fromName = titleCase(from)
    const toName = titleCase(to)
    const tier1 = isTier1Route(from, to)
    const label = `${fromName} to ${toName} cab`

    const inner = (
      <>
        <div className="pr-card__route">
          <span className="pr-card__city">{fromName}</span>
          <span className="pr-card__conn" aria-hidden="true">
            <span className="pr-card__dot" />
            <span className="pr-card__line" />
            <span className="pr-card__arrowhead">
              <ArrowIcon />
            </span>
          </span>
          <span className="pr-card__city">{toName}</span>
        </div>

        <div className="pr-card__meta">
          {facts?.distanceKm && (
            <span className="pr-card__chip"><RoadIcon /> {facts.distanceKm} km</span>
          )}
          {facts?.duration && (
            <span className="pr-card__chip"><ClockIcon /> {facts.duration}</span>
          )}
        </div>

        <div className="pr-card__foot">
          <div className="pr-card__price">
            {facts?.startingPrice ? (
              <>
                <span className="pr-card__from">from</span>
                <span className="pr-card__amount">{facts.startingPrice}</span>
                <span className="pr-card__incl">all inclusive</span>
              </>
            ) : (
              <span className="pr-card__amount pr-card__amount--sm">Book one way</span>
            )}
          </div>
          <span className="pr-card__cta">
            Book cab <ArrowIcon />
          </span>
        </div>
      </>
    )

    if (tier1) {
      return (
        <Link key={`${from}-${to}`} to={routePath(from, to)} className="pr-card" title={label}>
          {inner}
        </Link>
      )
    }
    return (
      <button
        key={`${from}-${to}`}
        type="button"
        className="pr-card"
        title={label}
        onClick={() => prefillSearch(from, to)}
      >
        {inner}
      </button>
    )
  }

  return (
    <section className="popular-routes-section">
      <div className="popular-routes-section__header">
        <span className="popular-routes-section__subtitle">Popular intercity routes</span>
        {/* This is the homepage's single keyword h1 (was in the removed
            RouteShowcase marquee). */}
        <h1 className="popular-routes-section__title">One Way Outstation Cab Booking Across India</h1>
        <p className="popular-routes-section__desc">
          {personalised
            ? `Fixed, all-inclusive fares on top routes from ${titleCase(citySlug)}`
            : "Book intercity cabs in 450+ cities at transparent per-kilometre fares, fixed and all-inclusive on one-way and round trips."}
        </p>
      </div>

      <div className="popular-routes-section__grid">
        {routes.map(renderCard)}
      </div>

      <button type="button" className="popular-routes-section__all" onClick={scrollToSearch}>
        Book any other route <ArrowIcon />
      </button>
    </section>
  )
}

export default PopularRoutes
